import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BandBadge, CvBackedMark, CvBandBadge, NOT_YET_EVIDENCED_LABEL, PercentLabel, RequirementState } from 'agu_app_shell';
import {
    ResearchCareerNarrative, ResearchJobMatch, ResearchJobProfile, ResearchMatchedRequirement, ResearchProfileShare, ResearchRequirementRef, ResearchRoleMatch,
} from '../../models/research.models';
import { FormatJobAge, FormatSalaryLine } from '../job-finder/job-card.component';
import { PostingsOf, SkillCoverage } from './career-explorer.state';
import type { OpeningsState } from './career-explorer.state';
import { SalaryFactLabel } from '../job-finder/job-detail.component';

/** P6:532 `list.slice(0,6)`: openings shown in the right column. */
export const MAX_OPENINGS = 6;

/** P6:541 (the first sentence; the "Browse all jobs" link that follows it is a button in the template). */
export const EMPTY_OPENINGS_COPY = 'No listings in Job Finder match this path right now.';

/** Shown only when the Job Finder search itself failed: never the empty copy, which would claim there are no listings. */
export const OPENINGS_UNAVAILABLE_COPY = 'Listings could not be loaded right now.';

export const NARRATIVE_LABEL = 'AI-written from your match results';

/** The note on a blocking credential (brief): a credential most postings require that the CV does not evidence. */
export const BLOCKING_NOTE = 'Required by most postings';

/** One row of the Skills or Credentials list (P6:1665-1670 `row()`). */
export interface RequirementRow {
    readonly ProviderTagID: string;
    readonly Name: string;
    /** Disc filled teal with a check; true only for `evidenced`. */
    readonly Met: boolean;
    /** `small` text under the name: "via X" / "from your CV" / the blocking note; null when there is none. */
    readonly Sub: string | null;
    /** The hover text: the provider's rationale for a partial match. */
    readonly Title: string | null;
    /** "not yet evidenced" for a row that is not met (never "missing"); null for a met row. */
    readonly State: typeof NOT_YET_EVIDENCED_LABEL | null;
    /** A row that is not met can be marked "I have this". */
    readonly CanAssert: boolean;
}

export interface OpeningRow {
    readonly JobID: string;
    readonly Title: string;
    readonly IsNew: boolean;
    /** "{org} · {age}" — either half left out when the posting does not carry it. */
    readonly Org: string;
    readonly Salary: string | null;
    readonly SalaryNote: string | null;
}

export interface RoleView {
    readonly Title: string;
    readonly Badge: CvBandBadge;
    readonly PercentLabel: string;
    /** The label without its `%`: P6:1660 sets the sign in a smaller `<small>`. */
    readonly PercentNumber: string;
    /** 0..100 for the bar's width. */
    readonly Percent: number;
    readonly Postings: string | null;
    readonly NarrativeText: readonly string[];
    readonly NarrativeLabel: string | null;
    readonly Skills: readonly RequirementRow[];
    readonly SkillsHave: number;
    readonly SkillsTotal: number;
    readonly Credentials: readonly RequirementRow[];
    readonly CredentialsHave: number;
    readonly EducationLine: string | null;
    readonly OpeningRows: readonly OpeningRow[];
}

const TopShare = (shares: readonly ResearchProfileShare[]): ResearchProfileShare | null =>
    shares.reduce<ResearchProfileShare | null>((top, s) => (top === null || s.share > top.share ? s : top), null);

/** A requirement CV evidence has not reached: not met, "not yet evidenced", and open to "I have this". */
function UnmetRow(ref: ResearchRequirementRef, sub: string | null): RequirementRow {
    return { ProviderTagID: ref.providerTagID, Name: ref.name, Met: false, Sub: sub, Title: null, State: NOT_YET_EVIDENCED_LABEL, CanAssert: true };
}

/**
 * A matched requirement. Only `evidenced` is met; a partial row (`evidenced: false` with a `matchedVia`) renders as not met, with the
 * provider's `rationale` as its title and "via {matchedVia.name}" under it. Only `cvBacked` says "from your CV".
 */
function MatchedRow(r: ResearchMatchedRequirement): RequirementRow {
    const state = RequirementState(r);
    const met = state === 'met';
    const parts = [r.matchedVia ? `via ${r.matchedVia.name}` : null, CvBackedMark(r)].filter((p): p is string => p !== null);
    const sub = parts.length > 0 ? parts.join(' · ') : null;
    return { ProviderTagID: r.providerTagID, Name: r.name, Met: met, Sub: sub, Title: r.rationale, State: met ? null : state, CanAssert: !met };
}

const EvidencedFirst = (rows: readonly RequirementRow[]): RequirementRow[] => [...rows.filter((r) => r.Met), ...rows.filter((r) => !r.Met)];

/**
 * The Career Explorer role view (P6:1657-1673 `vRole`, :1685-1707 `jobsBlock`): the score card, the AI narrative ("Why this role
 * fits"), the skills and credentials, and the Openings from Job Finder. Presentational: the page owns the data, asserts tags and
 * does the navigation (`OpenJob`, `SeeAllJobs`).
 *
 * Deviations from P6, each on purpose:
 * - P6:467-472 `hi`: the score card has no striped bar and no "Up to {hi}% once you review your pending tags" sentence (one overlap);
 * - P6:1669 lists each credential as "On your record: {evidence}"; the contract has no evidence text, so a credential row shows only
 *   met or "not yet evidenced" (and the blocking note);
 * - P6:1672/1693 list the skills a posting asks for under each opening (`jsk`) and the per-posting fit: the wire has no per-posting
 *   skill data, so an opening row is title, "New", org and age, and salary (spec §G).
 */
@Component({
    standalone: false,
    selector: 'ra-cx-role',
    templateUrl: './cx-role.component.html',
    styleUrl: './cx-role.component.scss',
})
export class CxRoleComponent {
    @Input() Match!: ResearchRoleMatch;
    @Input() Profile: ResearchJobProfile | null = null;
    /** The stored narrative, or null (not asked for yet, or it failed: the rest of the view shows without it). */
    @Input() Narrative: ResearchCareerNarrative | null = null;
    @Input() NarrativeLoading = false;
    /** Live Job Finder listings for this path; null until asked for. */
    @Input() Openings: readonly ResearchJobMatch[] | null = null;
    /** `idle` (waiting on the role's profile) and `loading` both show the skeleton; only `failed` (a real `SearchJobs` failure) says the listings could not be loaded. */
    @Input() OpeningsState: OpeningsState = 'idle';

    @Output() readonly Assert = new EventEmitter<string>();
    @Output() readonly OpenJob = new EventEmitter<string>();
    @Output() readonly SeeAllJobs = new EventEmitter<void>();
    /** P6:1702: the empty copy's "Browse all jobs" opens the plain finder, not this path's query. */
    @Output() readonly BrowseAllJobs = new EventEmitter<void>();

    readonly EmptyOpeningsCopy = EMPTY_OPENINGS_COPY;
    readonly OpeningsUnavailableCopy = OPENINGS_UNAVAILABLE_COPY;
    readonly BlockingNote = BLOCKING_NOTE;

    private memo: {
        match: ResearchRoleMatch; profile: ResearchJobProfile | null; narrative: ResearchCareerNarrative | null; openings: readonly ResearchJobMatch[] | null; view: RoleView;
    } | null = null;

    /** Recomputed only when an input changes identity: the template reads it on every pass (a fresh array each time repaints in a loop). */
    get View(): RoleView {
        const m = this.memo;
        if (m === null || m.match !== this.Match || m.profile !== this.Profile || m.narrative !== this.Narrative || m.openings !== this.Openings) {
            this.memo = { match: this.Match, profile: this.Profile, narrative: this.Narrative, openings: this.Openings, view: this.Build() };
        }
        return this.memo!.view;
    }

    get ShowOpeningsSkeleton(): boolean {
        return this.OpeningsState === 'idle' || this.OpeningsState === 'loading';
    }

    get ShowEmptyOpenings(): boolean {
        return this.OpeningsState === 'done' && (this.Openings?.length ?? 0) === 0;
    }

    get ShowOpeningsUnavailable(): boolean {
        return this.OpeningsState === 'failed';
    }

    private Build(): RoleView {
        const match = this.Match;
        const profile = this.Profile;
        const label = PercentLabel(match.overlap);
        const skills = EvidencedFirst([...match.skills.map(MatchedRow), ...match.notYetEvidenced.map((r) => UnmetRow(r, null))]);
        const credentials = EvidencedFirst([
            ...match.education.map(MatchedRow),
            ...match.credentials.map(MatchedRow),
            ...match.blockingCredentials.map((r) => UnmetRow(r, BLOCKING_NOTE)),
        ]);
        const coverage = SkillCoverage(match);
        const top = profile ? TopShare(profile.educationProfile) : null;
        const narrative = this.Narrative;
        return {
            Title: match.title,
            Badge: BandBadge(match.band),
            PercentLabel: label,
            PercentNumber: String(Number.parseInt(label, 10)),
            Percent: Number.parseInt(label, 10),
            Postings: profile ? `Based on ${PostingsOf(profile).toLocaleString('en-US')} current postings for this occupation.` : null,
            NarrativeText: narrative ? narrative.text.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 0) : [],
            NarrativeLabel: narrative?.generated === true ? NARRATIVE_LABEL : null,
            Skills: skills,
            SkillsHave: coverage.have,
            SkillsTotal: coverage.total,
            Credentials: credentials,
            CredentialsHave: credentials.filter((r) => r.Met).length,
            EducationLine: top ? `${Math.round(top.share * 100)}% of postings ask for this level of education` : null,
            OpeningRows: (this.Openings ?? []).slice(0, MAX_OPENINGS).map(OpeningOf),
        };
    }

    IHaveThis(providerTagID: string): void {
        this.Assert.emit(providerTagID);
    }

    OpenOne(jobID: string): void {
        this.OpenJob.emit(jobID);
    }

    OpenAll(): void {
        this.SeeAllJobs.emit();
    }

    OpenBrowse(): void {
        this.BrowseAllJobs.emit();
    }
}

/** P6:1693-1696: title, "New", "{org} · {age}", salary and its note. Reuses the Job Finder's own salary and age formatters. */
function OpeningOf(job: ResearchJobMatch): OpeningRow {
    const hasSalary = job.salaryMinAnnual !== null || job.salaryMaxAnnual !== null;
    return {
        JobID: job.jobId,
        Title: job.title,
        IsNew: job.isNew,
        Org: [job.companyName, FormatJobAge(job)].filter((p): p is string => p !== null && p !== '').join(' · '),
        Salary: hasSalary ? FormatSalaryLine(job) : null,
        // P6:1695 always says "Est. salary" (its salaries are estimates); a caption only goes under a salary the wire calls an estimate.
        SalaryNote: hasSalary && job.salaryIsEstimated === true ? SalaryFactLabel(true) : null,
    };
}
